import React, { useState, useEffect, useRef, useCallback } from 'react';
import Sketch from '@uiw/react-color-sketch';
import {
	Form,
	Input,
	Button,
	Select,
	InputNumber,
	Checkbox,
	Radio,
	Slider,
	Row,
	Col,
	Typography,
	Card,
	Space,
	Modal,
	Spin,
	Upload,
	message,
} from 'antd';
import {
	ICON_COMPLETE_MAP,
	MAPPED_BADGE_TYPE_1,
} from 'src/RnRCommonComponent/CoreBadge/rewardsConstants';
import BadgeIcon from 'src/RnRCommonComponent/CoreBadge/BadgeIcon/BadgeIcon';
import { motion } from 'motion/react';
import { CoreBadge } from 'src/RnRCommonComponent';
import NeoBadge from 'src/RnRCommonComponent/CoreBadge/BadgeIcon/NeoBadge';
import { useSelector } from 'react-redux';
import { useApolloClient, useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { fetchS3Bytes } from 'src/certificate-generator/certificatePdfUtils';
import { uploadToS3 } from 'src/_shared/services/utils.js';
import html2canvas from 'html2canvas';
import { QUERY_REWARDS_STORE_REWARDS } from 'src/rewardsStore/RewardsStoreGraphQL';
import uuid from 'uuid/v4';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

const UPDATE_BADGE = gql`
	mutation UpdateBadge($input: UpdateBadgeInput!) {
		updateBadge(input: $input) {
			id
			allowSelfNomination
			assignmentLevelId
			assignmentLimit
			badgeId
			badgeLimitPerUser {
				limit
				frequency
			}
			badgeTypeId
			bonusPoints
			color
			createdBy
			description
			gradientColor
			iconId
			isPreApproved
			label
			milestoneYears
			name
			points
			recipientLevelId
			snappyGrantId
			snappyGrant {
				id
				name
				description
				costInCurrency
			}
			rewardBonus
			rewardMaxBonus
			updatedBy
			visibility
			certificateConfig {
				id
				name
				key
			}
			milestoneRewardId
			shape
			customImageKey
			customImageSignedUrl
			badgeRotation
			labelColor
			customIconKey
			customIconSignedUrl
			milestoneReward {
				id
				name
				image
			}
		}
	}
`;

const CREATE_BADGE = gql`
	mutation CreateBadge($input: CreateBadgeInput!) {
		createBadge(input: $input) {
			id
			allowSelfNomination
			assignmentLevelId
			assignmentLimit
			badgeId
			badgeLimitPerUser {
				limit
				frequency
			}
			badgeTypeId
			bonusPoints
			color
			createdBy
			description
			gradientColor
			iconId
			isPreApproved
			label
			milestoneYears
			name
			points
			recipientLevelId
			snappyGrantId
			snappyGrant {
				id
				name
				description
				costInCurrency
			}
			rewardBonus
			rewardMaxBonus
			updatedBy
			visibility
			certificateConfig {
				id
				name
				key
			}
			shape
			badgeRotation
			customImageKey
			customImageSignedUrl
			labelColor
			customIconKey
			customIconSignedUrl
			milestoneRewardId
			milestoneReward {
				id
				name
				image
			}
		}
	}
`;

const LIST_CERTIFICATE_ASSETS = gql`
	query ListCertificateAssets($companyId: ID!) {
		listCertificateAssets(companyId: $companyId) {
			configs {
				key
				fileName
			}
		}
	}
`;

const BadgeFactory = {
	/**
	 * Creates a new badge object with default values
	 * @param {Object} properties - Badge properties to override defaults
	 * @returns {Object} - A complete badge object
	 */
	createBadge: (properties = {}) => {
		const defaultIconInfo = ICON_COMPLETE_MAP.find(
			(icon) => icon.code === 'AWARD'
		);

		// Default badge values
		const defaultBadge = {
			id: uuid(),
			name: 'New Badge',
			description: 'Badge description',
			label: 'Label',
			color: '#4338CA',
			gradientColor: '#6366F1',
			labelColor: '#000000E6',
			shape: 'hexagon',
			badgeType: 'PEERTOPEER',
			category: 'Default',
			isPreApproved: true,
			isAssignmentRestricted: false,
			visibility: true,
			iconInfo: defaultIconInfo,
		};

		return { ...defaultBadge, ...properties };
	},

	/**
	 * Creates a nomination badge
	 * @param {Object} properties - Badge properties to override defaults
	 * @returns {Object} - A nomination badge object
	 */
	createNominationBadge: (properties = {}) => {
		const nomBadge = BadgeFactory.createBadge({
			badgeType: 'NOMINATED',
			shape: 'hexagon',
			isPreApproved: false,
			...properties,
		});

		return nomBadge;
	},

	/**
	 * Creates a standard badge for on-the-spot recognition
	 * @param {Object} properties - Badge properties to override defaults
	 * @returns {Object} - A standard badge object
	 */
	createStandardBadge: (properties = {}) => {
		return BadgeFactory.createBadge({
			badgeType: 'PEERTOPEER',
			shape: 'hexagon',
			...properties,
		});
	},

	/**
	 * Returns predefined icon options for badges
	 * @returns {Array} - Array of icon objects with name and component
	 */
	getIconOptions: () => {
		return ICON_COMPLETE_MAP.map((iconData) => {
			return {
				id: iconData.id,
				iconInfo: iconData,
				code: iconData.code,
				name: iconData.name,
				component: <BadgeIcon size={24} iconName={iconData.code} />,
			};
		});
	},

	/**
	 * Gets predefined badge categories
	 * @returns {Array} - Array of category options
	 */
	getBadgeCategories: () => {
		const currentUser = useSelector((state) => state.user.currentUser);
		const recognitionCategories =
			currentUser?.company?.recognitionSettings?.recognitionCategories || [];
		return ['Default', ...recognitionCategories];
	},

	/**
	 * Gets predefined badge shapes
	 * @returns {Array} - Array of shape options
	 */
	getBadgeShapes: (badgeType) => {
		if (badgeType === 'AUTOMATED') {
			return [
				{ value: 'shield', label: 'Shield' },
				{ value: 'custom', label: 'Custom Image' },
			];
		}

		return [
			{ value: 'hexagon', label: 'Hexagon' },
			{ value: 'circle', label: 'Circle' },
			{ value: 'flower', label: 'Flower' },
			{ value: 'wavy', label: 'Wavy' },
			{ value: 'starburst', label: 'Starburst' },
			{ value: 'custom', label: 'Custom Image' },
		];
	},
};

// Canvas clip-path helpers mirroring the CSS shapes in NeoBadge.css.
// Coordinates are percentage [0–100] of the element, scaled to canvas pixels.
const SHAPE_POLYGONS = {
	hexagon: [
		[98.66, 45],
		[99.4, 46.58],
		[99.85, 48.26],
		[100, 50],
		[99.85, 51.74],
		[99.4, 53.42],
		[98.66, 55],
		[78.66, 89.64],
		[77.66, 91.07],
		[76.43, 92.3],
		[75, 93.3],
		[73.42, 94.04],
		[71.74, 94.49],
		[70, 94.64],
		[30, 94.64],
		[28.26, 94.49],
		[26.58, 94.04],
		[25, 93.3],
		[23.57, 92.3],
		[22.34, 91.07],
		[21.34, 89.64],
		[1.34, 55],
		[0.6, 53.42],
		[0.15, 51.74],
		[0, 50],
		[0.15, 48.26],
		[0.6, 46.58],
		[1.34, 45],
		[21.34, 10.36],
		[22.34, 8.93],
		[23.57, 7.7],
		[25, 6.7],
		[26.58, 5.96],
		[28.26, 5.51],
		[30, 5.36],
		[70, 5.36],
		[71.74, 5.51],
		[73.42, 5.96],
		[75, 6.7],
		[76.43, 7.7],
		[77.66, 8.93],
		[78.66, 10.36],
	],
	starburst: [
		[99.52, 56.89],
		[85.07, 67.04],
		[86.02, 84.68],
		[68.36, 84.41],
		[58.75, 99.23],
		[44.63, 88.62],
		[28.14, 94.97],
		[22.95, 78.09],
		[5.89, 73.54],
		[11.6, 56.83],
		[0.48, 43.11],
		[14.93, 32.96],
		[13.98, 15.32],
		[31.64, 15.59],
		[41.25, 0.77],
		[55.37, 11.38],
		[71.86, 5.03],
		[77.05, 21.91],
		[94.11, 26.46],
		[88.4, 43.18],
	],
	shield: [
		[0, 0],
		[100, 0],
		[100, 75],
		[50, 100],
		[0, 75],
	],
};

// Flower mask: 12 petal circles + central disc.
// CSS mask images are 20.56% wide, radial-gradient fills 71% of farthest-corner.
// Petal radius = 0.71 * sqrt(2)/2 * 0.2056 ≈ 0.1032 of element size.
// Petal centers computed via CSS background-position formula:
//   center = (1 - 0.2056) * position + 0.2056 / 2
const FLOWER_PETALS = [
	[0.897, 0.5],
	[0.844, 0.699],
	[0.699, 0.844],
	[0.5, 0.897],
	[0.301, 0.844],
	[0.156, 0.699],
	[0.103, 0.5],
	[0.156, 0.301],
	[0.301, 0.156],
	[0.5, 0.103],
	[0.699, 0.156],
	[0.844, 0.301],
];
const FLOWER_PETAL_R = 0.1032;
const FLOWER_DISC_R = 0.38366;

// Wavy mask: inner ring of 10 bumps, outer ring of 10 bumps, central disc.
// CSS subtracts the central disc from the inner ring, then adds outer ring.
// Bump image size = s*0.169, radial-gradient(50% 50%, ...) → radius = 0.5 * 0.169 * 0.99.
const WAVY_BUMP_R = 0.0837;
const WAVY_DISC_R = 0.429;
const WAVY_INNER = [
	[0.888, 0.5],
	[0.814, 0.728],
	[0.62, 0.869],
	[0.38, 0.869],
	[0.186, 0.728],
	[0.112, 0.5],
	[0.186, 0.272],
	[0.38, 0.131],
	[0.62, 0.131],
	[0.814, 0.272],
];
const WAVY_OUTER = [
	[0.964, 0.651],
	[0.787, 0.895],
	[0.5, 0.988],
	[0.213, 0.895],
	[0.036, 0.651],
	[0.036, 0.349],
	[0.213, 0.105],
	[0.5, 0.012],
	[0.787, 0.105],
	[0.964, 0.349],
];

/** Draw a clip-path shape into the current canvas path. */
function drawBadgeClipPath(ctx, shape, w, h) {
	const polygon = SHAPE_POLYGONS[shape];
	if (polygon) {
		ctx.moveTo((polygon[0][0] / 100) * w, (polygon[0][1] / 100) * h);
		for (let i = 1; i < polygon.length; i++) {
			ctx.lineTo((polygon[i][0] / 100) * w, (polygon[i][1] / 100) * h);
		}
		ctx.closePath();
		return;
	}

	if (shape === 'circle') {
		ctx.arc(w / 2, h / 2, w * 0.45, 0, Math.PI * 2);
		return;
	}

	if (shape === 'flower') {
		// Central disc + overlapping petal circles form a single unioned shape
		ctx.arc(w / 2, h / 2, FLOWER_DISC_R * w, 0, Math.PI * 2);
		const r = FLOWER_PETAL_R * w;
		for (const [px, py] of FLOWER_PETALS) {
			ctx.moveTo(px * w + r, py * h);
			ctx.arc(px * w, py * h, r, 0, Math.PI * 2);
		}
		return;
	}

	if (shape === 'wavy') {
		// Wavy = central disc + inner bumps (peaks), with outer bumps
		// carving the valleys. Can't do subtract with clip, so handled
		// separately in applyShapeToCanvas via canvas compositing.
		return;
	}

	// Fallback: full rectangle
	ctx.rect(0, 0, w, h);
}

/** Apply shape clipping to a source canvas and return the result. */
function applyShapeToCanvas(sourceCanvas, shape, rotationDeg) {
	const w = sourceCanvas.width;
	const h = sourceCanvas.height;
	const out = document.createElement('canvas');
	out.width = w;
	out.height = h;
	const ctx = out.getContext('2d');

	if (shape === 'wavy') {
		// Wavy = disc + inner bumps (peaks), then outer bumps carve
		// the valleys via subtraction. Build a mask canvas with
		// compositing, then apply it to the source.
		const mask = document.createElement('canvas');
		mask.width = w;
		mask.height = h;
		const mctx = mask.getContext('2d');

		if (rotationDeg) {
			mctx.translate(w / 2, h / 2);
			mctx.rotate((rotationDeg * Math.PI) / 180);
			mctx.translate(-w / 2, -h / 2);
		}

		// 1. Draw disc + inner bumps (the solid shape)
		mctx.fillStyle = '#fff';
		mctx.beginPath();
		mctx.arc(w / 2, h / 2, WAVY_DISC_R * w, 0, Math.PI * 2);
		const r = WAVY_BUMP_R * w;
		for (const [px, py] of WAVY_INNER) {
			mctx.moveTo(px * w + r, py * h);
			mctx.arc(px * w, py * h, r, 0, Math.PI * 2);
		}
		mctx.fill();

		// 2. Subtract outer bumps to carve valleys
		mctx.globalCompositeOperation = 'destination-out';
		for (const [px, py] of WAVY_OUTER) {
			mctx.beginPath();
			mctx.arc(px * w, py * h, r, 0, Math.PI * 2);
			mctx.fill();
		}

		// Apply mask to source
		ctx.drawImage(sourceCanvas, 0, 0);
		ctx.globalCompositeOperation = 'destination-in';
		ctx.drawImage(mask, 0, 0);
		return out;
	}

	// All other shapes: use canvas clip path
	ctx.save();
	if (rotationDeg) {
		ctx.translate(w / 2, h / 2);
		ctx.rotate((rotationDeg * Math.PI) / 180);
		ctx.translate(-w / 2, -h / 2);
	}
	ctx.beginPath();
	drawBadgeClipPath(ctx, shape, w, h);
	ctx.clip();
	if (rotationDeg) {
		ctx.setTransform(1, 0, 0, 1, 0, 0);
	}
	ctx.drawImage(sourceCanvas, 0, 0);
	ctx.restore();
	return out;
}

/**
 * Badge Editor component for creating and editing badges
 * Used in the admin section for badge management
 */
export const RecognitionEditor = ({
	badge = null,
	onSave,
	onCancel,
	snappyGrants = [],
	visible = false,
}) => {
	const client = useApolloClient();
	const currentUser = useSelector((state) => state.user.currentUser);
	const [form] = Form.useForm();
	const [formData, setFormData] = useState(
		badge
			? {
					...badge,
					gradientColor: badge.gradientColor || badge.color,
					shape:
						!badge.shape ||
						(badge.badgeType === 'AUTOMATED' &&
							!['shield', 'custom'].includes(badge.shape))
							? badge.badgeType === 'AUTOMATED'
								? 'shield'
								: 'hexagon'
							: badge.shape,
					category: badge.category || 'Default',
					customIconSignedUrl: badge.customIconSignedUrl || null,
					badgeLimitPerUser: badge.badgeLimitPerUser || {
						limit: null,
						frequency: null,
					},
				}
			: {
					...BadgeFactory.createBadge(),
					badgeLimitPerUser: {
						limit: null,
						frequency: null,
					},
				}
	);
	const [previewKey, setPreviewKey] = useState(Date.now());
	const [customImagePreview, setCustomImagePreview] = useState(
		badge?.customImageSignedUrl || null
	);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [configOptions, setConfigOptions] = useState([]);
	const [loadingConfigs, setLoadingConfigs] = useState(false);
	const [milestoneRewards, setMilestoneRewards] = useState([]);
	const [activeColorPicker, setActiveColorPicker] = useState(null);

	const companyId = currentUser?.company?.id || currentUser?.companyId;
	const { data: assetsData } = useQuery(LIST_CERTIFICATE_ASSETS, {
		variables: { companyId },
		skip: !companyId,
	});

	const configKeysJson = JSON.stringify(
		(assetsData?.listCertificateAssets?.configs || []).map((c) => c.key)
	);
	useEffect(() => {
		const keys = JSON.parse(configKeysJson);
		if (keys.length === 0) {
			setConfigOptions([]);
			return;
		}

		let cancelled = false;
		setLoadingConfigs(true);
		Promise.all(
			keys.map(async (key) => {
				try {
					const bytes = await fetchS3Bytes(key);
					const json = JSON.parse(new TextDecoder().decode(bytes));
					return {
						label: json.name || key,
						value: json.id,
						configData: { id: json.id, name: json.name, key },
					};
				} catch {
					return {
						label: key,
						value: key,
						configData: { id: key, name: key, key },
					};
				}
			})
		)
			.then((options) => {
				if (!cancelled) {
					setConfigOptions(
						options.sort((a, b) => a.label.localeCompare(b.label))
					);
					setLoadingConfigs(false);
				}
			})
			.catch(() => {
				if (!cancelled) {
					setLoadingConfigs(false);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [configKeysJson]);

	useEffect(() => {
		if (companyId) {
			client
				.query({
					query: QUERY_REWARDS_STORE_REWARDS,
					variables: { companyId, rewardType: 'milestone', limit: 100 },
					fetchPolicy: 'cache-first',
				})
				.then(({ data }) => {
					const items =
						data?.queryRewardsStoreRewardsByCompanyIdRewardType?.items || [];
					setMilestoneRewards(items.filter((r) => r.active !== false));
				})
				.catch((err) =>
					console.error('Error fetching milestone rewards:', err)
				);
		}
	}, [companyId]);

	const previewRafRef = useRef(null);
	useEffect(() => {
		cancelAnimationFrame(previewRafRef.current);
		previewRafRef.current = requestAnimationFrame(() => {
			setPreviewKey(Date.now());
		});
		return () => cancelAnimationFrame(previewRafRef.current);
	}, [
		formData.color,
		formData.label,
		formData.gradientColor,
		formData.labelColor,
		formData.iconInfo?.code,
		formData.customIconSignedUrl,
		formData.shape,
	]);

	useEffect(() => {
		form.setFieldsValue(formData);
	}, [form, formData]);

	const colorRafRef = useRef(null);
	const handleColorChange = useCallback((field, value) => {
		cancelAnimationFrame(colorRafRef.current);
		colorRafRef.current = requestAnimationFrame(() => {
			setFormData((prev) => ({ ...prev, [field]: value }));
		});
	}, []);

	const handleChange = (field, value) => {
		const newFormData = {
			...formData,
			[field]: value,
		};
		if (field === 'badgeType') {
			if (
				value === 'AUTOMATED' &&
				!['shield', 'custom'].includes(newFormData.shape)
			) {
				newFormData.shape = 'shield';
			} else if (
				value !== 'AUTOMATED' &&
				['shield'].includes(newFormData.shape)
			) {
				newFormData.shape = 'hexagon';
			}
		}

		setFormData(newFormData);
	};

	const handleSubmit = async () => {
		if (isSubmitting) return;

		try {
			setIsSubmitting(true);
			const values = await form.validateFields();

			const isUpdate = badge?.id;

			const payload = {
				...formData,
				...values,
				companyId: currentUser?.company?.id || currentUser?.companyId,
			};

			if (isUpdate) {
				payload.id = badge.id;
				payload.badgeId = badge.id;
				payload.updatedBy = currentUser?.id;
			} else {
				payload.id = formData.id;
				payload.badgeId = formData.id;
				payload.createdBy = currentUser?.id;
			}

			if (payload.badgeType && MAPPED_BADGE_TYPE_1[payload.badgeType]) {
				payload.badgeTypeId = MAPPED_BADGE_TYPE_1[payload.badgeType];
			}

			if (formData.iconInfo?.id) {
				payload.iconId = formData.iconInfo.id;
			}

			// Remove any fields that shouldn't be sent to the API
			const propsToDelete = [
				'__typename',
				'createdAt',
				'updatedAt',
				'iconInfo',
				'snappyGrant',
				'badgeType',
				'deletedBy',
				'deletedAt',
				'externalWorkFlowId',
				'physicalAwardActivityId',
				'recipientPayTypeId',
				'isAssignmentSSORequired',
				'assignmentSSORequiredId',
				'isRewardRequiresDate',
				'isRecipientSSORequired',
				'recipientSSORequiredId',
				'automationRule',
				'icon',
				'active',
				'milestoneReward',
				'customImageSignedUrl',
				'customIconSignedUrl',
			];

			propsToDelete.forEach((prop) => {
				if (payload[prop]) {
					delete payload[prop];
				}
			});

			Object.keys(payload).forEach((key) => {
				if (
					key !== 'badgeTypeId' &&
					key !== 'certificateConfig' &&
					key !== 'milestoneRewardId' &&
					(payload[key] === null || payload[key] === undefined)
				) {
					delete payload[key];
				}
			});

			if (!isUpdate) {
				payload.visibility = payload.visibility !== false;
				payload.assignmentLimit = payload.assignmentLimit || 1;
				payload.rewardMaxBonus = payload.rewardMaxBonus || 0;
				payload.bonusPoints = payload.points || 0;
			}

			if (
				payload.snappyGrantId === null ||
				payload.snappyGrantId === undefined
			) {
				delete payload.snappyGrantId;
			}

			if (
				payload.certificateConfig &&
				typeof payload.certificateConfig === 'object'
			) {
				const { __typename, data, ...certConfig } = payload.certificateConfig;
				payload.certificateConfig = certConfig;
			}

			const mutation = isUpdate ? UPDATE_BADGE : CREATE_BADGE;
			const result = await client.mutate({
				mutation,
				variables: {
					input: payload,
				},
			});

			const resultData = result?.data?.updateBadge || result?.data?.createBadge;

			if (resultData) {
				// Generate and upload preview PNG to S3
				let previewUploadFailed = false;
				try {
					const badgeId = resultData.id;
					const previewBlob = await captureBadgePreview();
					if (previewBlob) {
						const key = `badges/${companyId}/previews/${badgeId}.png`;
						const file = new File([previewBlob], `${badgeId}.png`, {
							type: 'image/png',
						});
						await uploadToS3(file, key, 'erin-images');
					}
				} catch (previewError) {
					console.error('Error generating badge preview:', previewError);
					previewUploadFailed = true;
				}

				if (previewUploadFailed) {
					message.success(
						`Recognition ${isUpdate ? 'updated' : 'created'} successfully, but a static preview image could not be generated`
					);
				} else {
					message.success(
						`Recognition ${isUpdate ? 'updated' : 'created'} successfully`
					);
				}

				if (onSave) {
					onSave(resultData);
				}
				if (onCancel) {
					onCancel();
				}
			} else {
				message.error(
					`Failed to ${isUpdate ? 'update' : 'create'} recognition`
				);
			}
		} catch (error) {
			console.error(
				`Error ${badge?.id ? 'updating' : 'creating'} recognition:`,
				error
			);
			if (error.graphQLErrors && error.graphQLErrors.length > 0) {
				message.error(
					`Failed to ${badge?.id ? 'update' : 'create'} recognition: ${error.graphQLErrors[0].message}`
				);
			} else if (error.networkError) {
				message.error(
					'Network error. Please check your connection and try again.'
				);
			} else if (error.message) {
				message.error(
					`Failed to ${badge?.id ? 'update' : 'create'} recognition: ${error.message}`
				);
			} else {
				message.error(
					`Failed to ${badge?.id ? 'update' : 'create'} recognition. Please try again.`
				);
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	/**
	 * Captures the badge preview as a PNG Blob off-screen.
	 * Returns the Blob, or null if capture fails.
	 */
	const captureBadgePreview = async () => {
		const node = document.getElementById('badge-preview');
		if (!node) return null;

		// ---- Gather measurements from the LIVE DOM before cloning ----
		const origLabel = node.querySelector('.neo-badge-label');
		const origTextSpan = node.querySelector('.neo-badge-text');
		const nodeRect = node.getBoundingClientRect();
		let labelMetrics = null;
		if (origLabel && origTextSpan) {
			const labelRect = origLabel.getBoundingClientRect();
			const computed = window.getComputedStyle(origTextSpan);
			const labelComputed = window.getComputedStyle(origLabel);
			labelMetrics = {
				centerX: labelRect.left - nodeRect.left + labelRect.width / 2,
				centerY: labelRect.top - nodeRect.top + labelRect.height / 2,
				contentWidth:
					labelRect.width - parseFloat(labelComputed.paddingLeft) * 2,
				text: origTextSpan.textContent.trim(),
				fontSize: parseFloat(computed.fontSize),
				fontWeight: computed.fontWeight,
				fontFamily: computed.fontFamily,
				color: computed.color,
			};
		}

		// ---- Clone into an off-screen container ----
		const offscreen = document.createElement('div');
		offscreen.style.cssText =
			'position:fixed;left:-9999px;top:-9999px;pointer-events:none;';
		document.body.appendChild(offscreen);

		const clone = node.cloneNode(true);
		clone.removeAttribute('id');
		offscreen.appendChild(clone);

		try {
			// Flatten 3D transforms on the clone
			const flatten3D = (el) => {
				el.style.setProperty('transform-style', 'flat', 'important');
				el.style.setProperty('perspective', 'none', 'important');
				el.style.setProperty('translate', 'none', 'important');
				el.style.setProperty('rotate', 'none', 'important');
				el.style.setProperty('transform', 'none', 'important');
				for (const child of el.children) {
					flatten3D(child);
				}
			};
			flatten3D(clone);

			// Reset AutomateBadge preview scale/margins on clone
			clone.style.setProperty('scale', '1', 'important');
			clone.style.setProperty('margin-left', '0', 'important');
			clone.style.setProperty('margin-top', '0', 'important');

			// Detect badge type
			const directSvg = [...clone.children].find(
				(child) => child.tagName === 'svg'
			);
			const isCustom = formData.shape === 'custom' && customImagePreview;
			const needsShapeClip = !directSvg && !isCustom;

			const captureWidth = directSvg
				? Number.parseInt(directSvg.getAttribute('width'), 10) || 166
				: 260;
			const captureHeight = directSvg
				? Number.parseInt(directSvg.getAttribute('height'), 10) || 144
				: 260;

			const html2canvasOpts = {
				backgroundColor: null,
				scale: 2,
				useCORS: true,
				logging: false,
				width: captureWidth,
				height: captureHeight,
			};

			let finalCanvas;

			if (needsShapeClip) {
				const textSpan = clone.querySelector('.neo-badge-text');
				const snappy = clone.querySelector('.neo-badge-snappy-icon');
				if (textSpan)
					textSpan.style.setProperty('visibility', 'hidden', 'important');
				if (snappy) snappy.style.setProperty('display', 'none', 'important');

				// Pass 1: gradient shape only
				const shapeDiv = clone.querySelector('[class*="neo-badge-shape"]');
				const otherChildren = [...clone.children].filter((c) => c !== shapeDiv);
				for (const child of otherChildren)
					child.style.setProperty('visibility', 'hidden', 'important');
				const shapeCanvas = await html2canvas(clone, html2canvasOpts);
				for (const child of otherChildren)
					child.style.removeProperty('visibility');

				const clippedCanvas = applyShapeToCanvas(
					shapeCanvas,
					formData.shape,
					formData.badgeRotation || 0
				);

				// Pass 2: everything except gradient shape
				if (shapeDiv)
					shapeDiv.style.setProperty('visibility', 'hidden', 'important');
				const overlayCanvas = await html2canvas(clone, html2canvasOpts);

				// Composite
				finalCanvas = document.createElement('canvas');
				finalCanvas.width = clippedCanvas.width;
				finalCanvas.height = clippedCanvas.height;
				const octx = finalCanvas.getContext('2d');
				octx.drawImage(clippedCanvas, 0, 0);
				octx.drawImage(overlayCanvas, 0, 0);

				// Draw label text manually
				if (labelMetrics) {
					const pxRatio = 2;
					const centerX = labelMetrics.centerX * pxRatio;
					const centerY = labelMetrics.centerY * pxRatio;
					const fontSize = labelMetrics.fontSize * pxRatio;

					octx.font = `${labelMetrics.fontWeight} ${fontSize}px ${labelMetrics.fontFamily}`;
					octx.fillStyle = labelMetrics.color;
					octx.textAlign = 'center';
					octx.textBaseline = 'middle';

					const maxWidth = labelMetrics.contentWidth * pxRatio;
					const words = labelMetrics.text.split(/\s+/);
					const lines = [];
					let currentLine = words[0];
					for (let i = 1; i < words.length; i++) {
						const testLine = currentLine + ' ' + words[i];
						if (octx.measureText(testLine).width > maxWidth) {
							lines.push(currentLine);
							currentLine = words[i];
						} else {
							currentLine = testLine;
						}
					}
					lines.push(currentLine);

					const lineHeight = fontSize * 1.1;
					const blockHeight = lines.length * lineHeight;
					const startY =
						centerY - blockHeight / 2 + lineHeight / 2 + fontSize * 0.1;
					for (let i = 0; i < lines.length; i++) {
						octx.fillText(lines[i], centerX, startY + i * lineHeight);
					}
				}
			} else {
				// AutomateBadge or custom image
				if (isCustom) {
					for (const img of clone.querySelectorAll('img')) {
						const natural = img.naturalWidth / img.naturalHeight;
						const box = 260;
						let w, h;
						if (natural > 1) {
							w = box;
							h = box / natural;
						} else {
							h = box;
							w = box * natural;
						}
						img.style.setProperty('width', `${w}px`, 'important');
						img.style.setProperty('height', `${h}px`, 'important');
						img.style.setProperty('object-fit', 'fill', 'important');
						img.style.setProperty('margin', 'auto', 'important');
					}
				}
				finalCanvas = await html2canvas(clone, html2canvasOpts);
			}

			// Convert canvas to Blob
			return await new Promise((resolve) => {
				finalCanvas.toBlob((blob) => resolve(blob), 'image/png');
			});
		} finally {
			document.body.removeChild(offscreen);
		}
	};

	const iconOptions = BadgeFactory.getIconOptions();
	const shapeOptions = BadgeFactory.getBadgeShapes(formData.badgeType);
	const categoryOptions = BadgeFactory.getBadgeCategories();

	return (
		<Modal
			centered
			title={badge ? 'Update Recognition' : 'Create Recognition'}
			open={visible}
			onCancel={onCancel}
			width={1000}
			style={{ top: 20 }}
			footer={[
				<Button style={{ marginRight: 12 }} key="cancel" onClick={onCancel}>
					Cancel
				</Button>,
				<motion.div key="save" style={{ display: 'inline-block' }}>
					<Button
						type="primary"
						onClick={handleSubmit}
						loading={isSubmitting}
						disabled={isSubmitting}
					>
						{isSubmitting
							? badge?.id
								? 'Updating...'
								: 'Creating...'
							: badge?.id
								? 'Update Recognition'
								: 'Create Recognition'}
					</Button>
				</motion.div>,
			]}
		>
			<div
				style={{ maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden' }}
			>
				<Row gutter={24}>
					{/* Preview Panel */}
					<Col xs={24} md={10}>
						<div
							style={{
								position: 'sticky',
								top: '0',
							}}
						>
							<Card
								title="Recognition Preview"
								style={{
									backgroundColor: '#ffffff',
									borderRadius: '12px',
									border: '1px solid #e2e8f0',
									boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
								}}
							>
								<div
									className="add-reward-preview"
									style={{
										display: 'flex',
										justifyContent: 'center',
										padding: '16px 0',
									}}
								>
									<div style={{ height: '260px', width: '260px' }}>
										{formData?.shape === 'custom' && customImagePreview ? (
											<NeoBadge
												captureId={'badge-preview'}
												customImage={customImagePreview}
												useTilt={true}
											/>
										) : (
											<CoreBadge
												captureId={'badge-preview'}
												badgeType={formData?.badgeType}
												badgeIcon={formData?.iconInfo?.code || 'AWARD'}
												label={formData?.label || ''}
												color={formData?.color || '#0000FF'}
												shape={formData?.shape || 'hexagon'}
												badgeRotation={formData?.badgeRotation || 0}
												hideSnappyIcon={false}
												labelColor={formData?.labelColor || null}
												customIconSignedUrl={
													formData?.customIconSignedUrl || null
												}
												snappyDetails={{
													snappyGrantId: formData?.snappyGrantId,
													description: formData?.snappyGrant?.description,
													snappyGrantName:
														formData?.snappyGrant?.name || 'Snappy Reward',
													costInCurrency: `${formData?.snappyGrant?.costInCurrency || '0'}`,
												}}
												gradientColor={
													formData?.gradientColor || formData?.color
												}
											/>
										)}
									</div>
								</div>

								<div style={{ textAlign: 'center', marginTop: '16px' }}>
									<Title
										level={5}
										style={{ margin: '0 0 8px 0', color: '#0f172a' }}
									>
										{formData.name || 'Recognition Name'}
									</Title>
									<Text
										style={{
											fontSize: '14px',
											color: '#475569',
											display: 'block',
											marginBottom: '8px',
										}}
									>
										{formData.description || 'Recognition description'}
									</Text>
									<Space style={{ marginTop: '8px' }}>
										<span
											style={{
												fontSize: '12px',
												fontWeight: 500,
												padding: '4px 8px',
												backgroundColor: '#f1f5f9',
												color: '#334155',
												borderRadius: '9999px',
											}}
										>
											{formData.category || 'Category'}
										</span>
										<span
											style={{
												fontSize: '12px',
												fontWeight: 500,
												padding: '4px 8px',
												backgroundColor: '#f1f5f9',
												color: '#334155',
												borderRadius: '9999px',
											}}
										>
											{formData.badgeType === 'PEERTOPEER'
												? 'On-The-Spot'
												: formData.badgeType === 'NOMINATED'
													? 'Nominated'
													: 'Milestones'}
										</span>
									</Space>
								</div>
							</Card>
						</div>
					</Col>

					<Col xs={24} md={14}>
						<Form form={form} layout="vertical" onFinish={handleSubmit}>
							<Row gutter={24}>
								<Col xs={24} md={12}>
									<Title
										level={4}
										style={{ color: '#0f172a', marginBottom: '24px' }}
									>
										Recognition Details
									</Title>

									<Form.Item
										label="Name"
										name="name"
										rules={[
											{
												required: true,
												message: 'Please enter a Recognition name',
											},
										]}
									>
										<Input
											value={formData.name}
											onChange={(e) => handleChange('name', e.target.value)}
										/>
									</Form.Item>

									<Form.Item
										label="Label"
										name="label"
										rules={[
											{
												required: true,
												message: 'Please enter a Recognition label',
											},
										]}
									>
										<Input
											value={formData.label}
											onChange={(e) => handleChange('label', e.target.value)}
										/>
									</Form.Item>

									<Form.Item label="Description" name="description">
										<TextArea
											rows={4}
											value={formData.description || ''}
											onChange={(e) =>
												handleChange('description', e.target.value)
											}
										/>
									</Form.Item>

									<Form.Item label="Category" name="category">
										<Select
											value={formData.category}
											onChange={(value) => handleChange('category', value)}
										>
											{categoryOptions.map((category) => (
												<Option key={category} value={category}>
													{category}
												</Option>
											))}
										</Select>
									</Form.Item>

									<Form.Item label="Recognition Type" name="badgeType">
										<Select
											value={formData.badgeType}
											onChange={(value) => handleChange('badgeType', value)}
										>
											<Option value="PEERTOPEER">On-The-Spot</Option>
											<Option value="NOMINATED">Nominated</Option>
											<Option value="AUTOMATED">Milestones</Option>
										</Select>
									</Form.Item>
								</Col>

								<Col xs={24} md={12}>
									<Title
										level={4}
										style={{ color: '#0f172a', marginBottom: '24px' }}
									>
										Appearance
									</Title>

									<Form.Item label="Shape" name="shape">
										<Select
											value={formData.shape}
											onChange={(value) => handleChange('shape', value)}
										>
											{shapeOptions.map((shape) => (
												<Option key={shape.value} value={shape.value}>
													{shape.label}
												</Option>
											))}
										</Select>
									</Form.Item>

									{formData.shape === 'custom' ? (
										<Form.Item label="Custom Badge Image">
											<Upload
												accept=".png"
												showUploadList={false}
												beforeUpload={(file) => {
													if (file.size > 1024 * 1024) {
														message.error('File must be under 1MB');
														return false;
													}

													const reader = new FileReader();
													reader.onload = (e) => {
														setCustomImagePreview(e.target.result);
													};
													reader.readAsDataURL(file);

													const badgeId = formData.id || badge?.id;
													const key = `badges/${companyId}/${badgeId}.png`;
													uploadToS3(file, key, 'erin-images')
														.then(() => {
															handleChange('customImageKey', key);
														})
														.catch((err) => {
															console.error(
																'Error uploading custom image:',
																err
															);
															message.error('Failed to upload image');
														});

													return false;
												}}
											>
												<Button>Upload PNG</Button>
											</Upload>
											{customImagePreview && (
												<Button
													type="link"
													danger
													style={{ marginLeft: 8 }}
													onClick={() => {
														setCustomImagePreview(null);
														handleChange('customImageKey', null);
													}}
												>
													Remove
												</Button>
											)}
										</Form.Item>
									) : formData.shape !== 'shield' ? (
										<Form.Item
											label={`Rotation: ${formData.badgeRotation || 0}°`}
											style={{ overflow: 'hidden' }}
										>
											<div style={{ padding: '0 6px' }}>
												<Slider
													min={0}
													max={360}
													value={formData.badgeRotation || 0}
													onChange={(value) =>
														handleChange('badgeRotation', value)
													}
												/>
											</div>
										</Form.Item>
									) : null}

									<Row gutter={16}>
										<Col span={12}>
											<Form.Item label="Primary Color" name="color">
												<div>
													<button
														type="button"
														onClick={() =>
															setActiveColorPicker((prev) =>
																prev === 'color' ? null : 'color'
															)
														}
														style={{
															width: '100%',
															height: 32,
															border: '1px solid #d9d9d9',
															borderRadius: 6,
															cursor: 'pointer',
															background: formData.color,
														}}
													/>
													{activeColorPicker === 'color' && (
														<div style={{ position: 'relative' }}>
															<div
																style={{
																	position: 'fixed',
																	top: 0,
																	right: 0,
																	bottom: 0,
																	left: 0,
																}}
																onClick={() => setActiveColorPicker(null)}
															/>
															<div
																style={{
																	position: 'absolute',
																	zIndex: 10,
																	marginTop: 4,
																}}
															>
																<Sketch
																	color={formData.color}
																	onChange={(color) =>
																		handleColorChange('color', color.hex)
																	}
																	disableAlpha
																	presetColors={false}
																	style={{
																		boxShadow: '0 4px 16px rgba(0, 0, 0, 0.16)',
																		borderRadius: 8,
																	}}
																/>
															</div>
														</div>
													)}
												</div>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item label="Gradient Color" name="gradientColor">
												<div>
													<button
														type="button"
														disabled={formData.badgeType === 'AUTOMATED'}
														onClick={() =>
															setActiveColorPicker((prev) =>
																prev === 'gradientColor'
																	? null
																	: 'gradientColor'
															)
														}
														style={{
															width: '100%',
															height: 32,
															border: '1px solid #d9d9d9',
															borderRadius: 6,
															cursor:
																formData.badgeType === 'AUTOMATED'
																	? 'not-allowed'
																	: 'pointer',
															background:
																formData.gradientColor === null
																	? formData.color
																	: formData.gradientColor,
														}}
													/>
													{activeColorPicker === 'gradientColor' &&
														formData.badgeType !== 'AUTOMATED' && (
															<div style={{ position: 'relative' }}>
																<div
																	style={{
																		position: 'fixed',
																		top: 0,
																		right: 0,
																		bottom: 0,
																		left: 0,
																	}}
																	onClick={() => setActiveColorPicker(null)}
																/>
																<div
																	style={{
																		position: 'absolute',
																		zIndex: 10,
																		marginTop: 4,
																		right: 0,
																	}}
																>
																	<Sketch
																		color={
																			formData.gradientColor === null
																				? formData.color
																				: formData.gradientColor
																		}
																		onChange={(color) =>
																			handleColorChange(
																				'gradientColor',
																				color.hex
																			)
																		}
																		disableAlpha
																		presetColors={false}
																		style={{
																			boxShadow:
																				'0 4px 16px rgba(0, 0, 0, 0.16)',
																			borderRadius: 8,
																		}}
																	/>
																</div>
															</div>
														)}
												</div>
											</Form.Item>
										</Col>
									</Row>
									<Row gutter={16}>
										<Col span={12}>
											<Form.Item label="Label Color" name="labelColor">
												<div>
													<button
														type="button"
														disabled={formData.badgeType === 'AUTOMATED'}
														onClick={() =>
															setActiveColorPicker((prev) =>
																prev === 'labelColor' ? null : 'labelColor'
															)
														}
														style={{
															width: '100%',
															height: 32,
															border: '1px solid #d9d9d9',
															borderRadius: 6,
															cursor:
																formData.badgeType === 'AUTOMATED'
																	? 'not-allowed'
																	: 'pointer',
															background: formData.labelColor || '#000000cc',
														}}
													/>
													{activeColorPicker === 'labelColor' &&
														formData.badgeType !== 'AUTOMATED' && (
															<div style={{ position: 'relative' }}>
																<div
																	style={{
																		position: 'fixed',
																		top: 0,
																		right: 0,
																		bottom: 0,
																		left: 0,
																	}}
																	onClick={() => setActiveColorPicker(null)}
																/>
																<div
																	style={{
																		position: 'absolute',
																		zIndex: 10,
																		marginTop: 4,
																	}}
																>
																	<Sketch
																		color={formData.labelColor || '#000000cc'}
																		onChange={(color) =>
																			handleColorChange(
																				'labelColor',
																				color.hexa
																			)
																		}
																		presetColors={false}
																		style={{
																			boxShadow:
																				'0 4px 16px rgba(0, 0, 0, 0.16)',
																			borderRadius: 8,
																		}}
																	/>
																</div>
															</div>
														)}
												</div>
											</Form.Item>
										</Col>
									</Row>

									<Form.Item label="Icon">
										<div
											style={{
												display: 'grid',
												gridTemplateColumns: 'repeat(5, 1fr)',
												gap: '8px',
												padding: '12px',
												border: '1px solid #cbd5e1',
												borderRadius: '8px',
												maxHeight: '280px',
												overflowY: 'auto',
												opacity: formData.badgeType === 'AUTOMATED' ? 0.5 : 1,
												pointerEvents:
													formData.badgeType === 'AUTOMATED' ? 'none' : 'auto',
											}}
											className="recognition-icon-grid"
										>
											{iconOptions.map((iconOption, index) => (
												<div key={index} className="recognition-icon-option">
													<Button
														ghost
														type={
															formData?.iconInfo?.code === iconOption.code &&
															!formData.customIconSignedUrl
																? 'primary'
																: 'default'
														}
														onClick={() => {
															setFormData((prev) => ({
																...prev,
																iconInfo: iconOption.iconInfo,
																customIconSignedUrl: null,
															}));
														}}
														disabled={formData.badgeType === 'AUTOMATED'}
														style={{
															width: '40px',
															height: '40px',
															minWidth: '40px',
															padding: '0',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															borderRadius: '8px',
															border: 'none',
															background:
																formData?.iconInfo?.code === iconOption.code &&
																!formData.customIconSignedUrl
																	? formData.color + '1A'
																	: '#f8fafc',
														}}
													>
														{React.cloneElement(iconOption.component, {
															size: 20,
															color:
																formData?.iconInfo?.code === iconOption.code &&
																!formData.customIconSignedUrl
																	? formData.color
																	: '#334155',
														})}
													</Button>
												</div>
											))}
											{/* Custom Icon Upload */}
											<div className="recognition-icon-option">
												<Upload
													accept=".svg,.png"
													showUploadList={false}
													beforeUpload={(file) => {
														if (file.size > 512 * 1024) {
															message.error('Icon file must be under 512KB');
															return false;
														}

														const reader = new FileReader();
														reader.onload = (e) => {
															handleChange(
																'customIconSignedUrl',
																e.target.result
															);
														};
														reader.readAsDataURL(file);

														const ext = file.name
															.split('.')
															.pop()
															.toLowerCase();
														const badgeId = formData.id || badge?.id;
														const key = `badges/${companyId}/${badgeId}-icon.${ext}`;
														uploadToS3(file, key, 'erin-images')
															.then(() => {
																setFormData((prev) => ({
																	...prev,
																	customIconKey: key,
																}));
															})
															.catch((err) => {
																console.error(
																	'Error uploading custom icon:',
																	err
																);
																message.error('Failed to upload icon');
															});

														return false;
													}}
													disabled={formData.badgeType === 'AUTOMATED'}
												>
													<Button
														ghost
														type={
															formData.customIconSignedUrl
																? 'primary'
																: 'default'
														}
														disabled={formData.badgeType === 'AUTOMATED'}
														style={{
															width: '40px',
															height: '40px',
															minWidth: '40px',
															padding: '0',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															borderRadius: '8px',
															border: formData.customIconSignedUrl
																? `2px solid ${formData.color}`
																: '2px dashed #cbd5e1',
															background: formData.customIconSignedUrl
																? formData.color + '1A'
																: '#f8fafc',
															overflow: 'hidden',
														}}
													>
														{formData.customIconSignedUrl ? (
															<div
																style={{
																	width: 20,
																	height: 20,
																	backgroundColor: formData.color,
																	WebkitMaskImage: `url(${formData.customIconSignedUrl})`,
																	maskImage: `url(${formData.customIconSignedUrl})`,
																	WebkitMaskSize: 'contain',
																	maskSize: 'contain',
																	WebkitMaskRepeat: 'no-repeat',
																	maskRepeat: 'no-repeat',
																	WebkitMaskPosition: 'center',
																	maskPosition: 'center',
																}}
															/>
														) : (
															<span
																style={{
																	fontSize: '20px',
																	color: '#475569',
																	lineHeight: 1,
																}}
															>
																+
															</span>
														)}
													</Button>
												</Upload>
												{formData.customIconSignedUrl && (
													<Button
														type="link"
														danger
														size="small"
														style={{
															fontSize: '10px',
															padding: 0,
															height: 'auto',
															marginTop: '2px',
														}}
														onClick={() => {
															setFormData((prev) => ({
																...prev,
																customIconSignedUrl: null,
																customIconKey: null,
															}));
														}}
													>
														Remove
													</Button>
												)}
											</div>
										</div>
									</Form.Item>
								</Col>
							</Row>

							<div
								style={{
									paddingTop: '24px',
									borderTop: '1px solid #e2e8f0',
									marginTop: '24px',
								}}
							>
								<Row gutter={24}>
									<Col xs={24} md={12}>
										<Title
											level={4}
											style={{ color: '#0f172a', marginBottom: '16px' }}
										>
											Reward Settings
										</Title>

										{formData.badgeType === 'AUTOMATED' && (
											<Form.Item
												label="Milestone Years"
												name="milestoneYears"
												rules={[
													{
														required: true,
														message: 'Please enter milestone years',
													},
												]}
											>
												<InputNumber
													style={{ width: '100%' }}
													value={formData.milestoneYears || 0}
													onChange={(value) =>
														handleChange('milestoneYears', value)
													}
													min={1}
													placeholder="Enter years for milestone"
												/>
											</Form.Item>
										)}

										<Form.Item
											label="Points Awarded to Recipient"
											name="points"
										>
											<InputNumber
												style={{ width: '100%' }}
												value={formData.points || 0}
												onChange={(value) => handleChange('points', value)}
											/>
										</Form.Item>

										<Form.Item label="Snappy Reward">
											<Select
												style={{ width: '100%' }}
												value={formData.snappyGrantId}
												onChange={(value) =>
													handleChange('snappyGrantId', value)
												}
												placeholder="Select a Snappy reward"
												allowClear
												disabled={snappyGrants?.length === 0}
											>
												{snappyGrants.map((snappyGrant) => (
													<Option key={snappyGrant.id} value={snappyGrant.id}>
														{`${snappyGrant?.name || snappyGrant.id} - $${snappyGrant?.costInCurrency || 0}`}
													</Option>
												))}
											</Select>
										</Form.Item>

										<Form.Item label="Bonus Reward">
											<div style={{ marginBottom: '16px' }}>
												<Checkbox
													checked={formData.rewardBonus > 0}
													onChange={(e) => {
														if (e.target.checked) {
															handleChange(
																'rewardBonus',
																formData.rewardBonus || 100
															);
														} else {
															handleChange('rewardBonus', 0);
														}
													}}
												>
													Include Bonus Reward
												</Checkbox>
												{formData.rewardBonus > 0 && (
													<InputNumber
														style={{ width: '100%', marginTop: '8px' }}
														formatter={(value) =>
															`$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
														}
														parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
														value={formData.rewardBonus || 0}
														onChange={(value) =>
															handleChange('rewardBonus', value)
														}
														placeholder="Enter bonus amount"
													/>
												)}
											</div>
										</Form.Item>

										{(formData.badgeType === 'NOMINATED' ||
											formData.badgeType === 'AUTOMATED') &&
											configOptions.length > 0 && (
												<Form.Item label="Certificate Config">
													<Select
														placeholder={
															loadingConfigs
																? 'Loading configs...'
																: 'Select a certificate config'
														}
														options={configOptions}
														loading={loadingConfigs}
														disabled={loadingConfigs}
														notFoundContent={
															loadingConfigs ? (
																<Spin size="small" />
															) : (
																'No saved configs'
															)
														}
														value={formData.certificateConfig?.id ?? undefined}
														onChange={(val) => {
															if (!val) {
																handleChange('certificateConfig', null);
																return;
															}

															const opt = configOptions.find(
																(o) => o.value === val
															);
															if (opt) {
																handleChange(
																	'certificateConfig',
																	opt.configData
																);
															}
														}}
														allowClear
													/>
												</Form.Item>
											)}

										{formData.badgeType === 'AUTOMATED' && (
											<Form.Item label="Milestone Reward">
												<Select
													placeholder="Select a milestone reward"
													value={formData.milestoneRewardId || undefined}
													onChange={(val) =>
														handleChange('milestoneRewardId', val || null)
													}
													allowClear
													options={[
														...milestoneRewards.map((r) => ({
															label: r.name,
															value: r.id,
														})),
													]}
													notFoundContent="No milestone rewards available"
												/>
											</Form.Item>
										)}
									</Col>

									<Col xs={24} md={12}>
										<Title
											level={4}
											style={{ color: '#0f172a', marginBottom: '24px' }}
										>
											Permission Settings
										</Title>
										<Form.Item name="visibility">
											<Checkbox
												checked={formData.visibility}
												onChange={(e) =>
													handleChange('visibility', e.target.checked)
												}
											>
												Visibility -{' '}
												{formData.visibility ? (
													<span style={{ color: 'green' }}>Active</span>
												) : (
													<span style={{ color: '#595959' }}>Inactive</span>
												)}
											</Checkbox>
										</Form.Item>

										<Form.Item name="isPreApproved">
											<Checkbox
												checked={!formData.isPreApproved}
												onChange={(e) =>
													handleChange('isPreApproved', !e.target.checked)
												}
											>
												Require Admin Approval
											</Checkbox>
										</Form.Item>
										{formData.badgeType === 'NOMINATED' && (
											<Form.Item name="allowSelfNomination">
												<Checkbox
													checked={formData.allowSelfNomination}
													onChange={(e) =>
														handleChange(
															'allowSelfNomination',
															e.target.checked
														)
													}
												>
													Allow Self Nomination
												</Checkbox>
											</Form.Item>
										)}
										<Form.Item
											label="Allow Assignment from:"
											name="isAssignmentRestricted"
										>
											<Select
												value={formData.isAssignmentRestricted}
												onChange={(value) =>
													handleChange('isAssignmentRestricted', value)
												}
												disabled={formData.badgeType === 'AUTOMATED'}
											>
												<Option value={false}>All Employees</Option>
												<Option value={true}>Managers Only</Option>
											</Select>
										</Form.Item>

										<Form.Item
											label="Assignment Limit"
											name={['badgeLimitPerUser', 'limit']}
											tooltip="Maximum number of times this recognition can be assigned per user based on the selected frequency"
										>
											<InputNumber
												style={{ width: '100%' }}
												value={formData.badgeLimitPerUser?.limit}
												onChange={(value) => {
													const newBadgeLimitPerUser = {
														...formData.badgeLimitPerUser,
														limit: value,
													};
													if (value && !newBadgeLimitPerUser.frequency) {
														newBadgeLimitPerUser.frequency = 'allTime';
													}
													handleChange(
														'badgeLimitPerUser',
														newBadgeLimitPerUser
													);
												}}
												min={0}
												placeholder="Enter assignment limit"
											/>
										</Form.Item>

										<Form.Item
											label="Frequency"
											name={['badgeLimitPerUser', 'frequency']}
											tooltip="Select the time period(s) for the assignment limit"
											rules={[
												{
													validator: (_, value) => {
														const limit = form.getFieldValue([
															'badgeLimitPerUser',
															'limit',
														]);
														if (limit && !value) {
															return Promise.reject(
																new Error(
																	'Frequency is required when Assignment Limit is set'
																)
															);
														}
														return Promise.resolve();
													},
												},
											]}
										>
											<Select
												style={{ width: '100%' }}
												value={formData.badgeLimitPerUser?.frequency}
												onChange={(value) =>
													handleChange('badgeLimitPerUser', {
														...formData.badgeLimitPerUser,
														frequency: value,
													})
												}
												placeholder="Select frequency period"
												allowClear
											>
												<Option value="monthly">Monthly</Option>
												<Option value="quarterly">Quarterly</Option>
												<Option value="yearly">Yearly</Option>
												<Option value="allTime">All Time</Option>
											</Select>
										</Form.Item>
									</Col>
								</Row>
							</div>
						</Form>
					</Col>
				</Row>
			</div>
		</Modal>
	);
};
